/**
 * Reportes → índice (`/reportes`).
 *
 * Puerto de `Reportes::index` + `views/menu/reportes/index.php`: el panel anterior
 * era un **centro de reportes** con los reportes agrupados en seis familias
 * (General, Ventas, Liquidaciones, Contabilidad, Administrativos y Otros) y 45
 * enlaces en total.
 *
 * La pantalla nueva lista **el mismo catálogo** (`reportes-catalogo.ts`, con la
 * ruta heredada de cada reporte) y añade el estado de cada uno:
 *
 * - Los que ya tienen pantalla en el panel nuevo (`interno`) se abren aquí, con la
 *   descripción que el catálogo de módulos guarda en la base de datos.
 * - El resto (ninguno por ahora, pero la regla queda) enlaza al **panel anterior**
 *   con su candado, así que ningún reporte deja de ser accesible desde aquí.
 *
 * Las dos consultas SUNAT del índice anterior (comprobantes aceptados y no
 * aceptados entre dos fechas) hacían un POST al controlador de ventas y siguen
 * disponibles en el centro de reportes anterior, que se enlaza al pie.
 */

import { ArrowRight, ExternalLink, FileBarChart, Lock, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { buildLegacyUrl } from '@/lib/legacy-url';
import { formatDateTimeValue } from '@/lib/utils';

import { useIndiceReportes } from '../reportes.api';
import { CATALOGO_REPORTES, FAMILIAS, type ReporteDelCatalogo } from '../reportes-catalogo';

/** Texto sin acentos ni mayúsculas, para buscar sin pelearse con los tildes. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function ReportesPage() {
  useDocumentTitle('Reportes');

  const [busqueda, setBusqueda] = useState('');
  const consulta = useIndiceReportes();

  /** Descripción del catálogo de módulos para los reportes ya migrados. */
  const descripciones = useMemo(() => {
    const mapa = new Map<string, string>();

    for (const reporte of consulta.data?.reportes ?? []) {
      if (reporte.web_path && reporte.descripcion) {
        mapa.set(reporte.web_path, reporte.descripcion);
      }
    }

    return mapa;
  }, [consulta.data]);

  /** El catálogo filtrado por la búsqueda, agrupado por familia. */
  const porFamilia = useMemo(() => {
    const filtro = normalizar(busqueda.trim());
    const visibles = filtro
      ? CATALOGO_REPORTES.filter((reporte) => normalizar(reporte.nombre).includes(filtro))
      : CATALOGO_REPORTES;

    return FAMILIAS.map((familia) => ({
      familia,
      reportes: visibles.filter((reporte) => reporte.familia === familia),
    })).filter((grupo) => grupo.reportes.length > 0);
  }, [busqueda]);

  const migrados = CATALOGO_REPORTES.filter((reporte) => reporte.interno).length;
  const encontrados = porFamilia.reduce((total, grupo) => total + grupo.reportes.length, 0);

  const notaDe = (reporte: ReporteDelCatalogo) =>
    reporte.nota ?? (reporte.interno ? descripciones.get(reporte.interno) : undefined);

  // El panel anterior puede no estar configurado (`VITE_LEGACY_URL`): en ese caso
  // los reportes sin pantalla nueva se muestran sin enlace, no con uno roto.
  const centroAnterior = buildLegacyUrl('Reportes');

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Reportes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Centro de reportes: los {CATALOGO_REPORTES.length} reportes tienen ya su pantalla
            aquí; el centro anterior queda para sus dos consultas SUNAT.
          </p>
        </div>

        <Badge variant={migrados === CATALOGO_REPORTES.length ? 'success' : 'brand'}>
          {migrados} de {CATALOGO_REPORTES.length} reportes en el panel nuevo
        </Badge>
      </header>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el catálogo de módulos">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'El catálogo de reportes se muestra igual, con los enlaces del panel anterior.'}
        </Alert>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          <div className="min-w-[16rem] flex-1">
            <Input
              label="Buscar reporte"
              placeholder="Por ejemplo: descuento, visa, kardex, series…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
              leftIcon={<Search className="size-4" aria-hidden="true" />}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {busqueda ? (
              <Button variant="outline" onClick={() => setBusqueda('')}>
                Limpiar
              </Button>
            ) : null}
            {centroAnterior ? (
              <a href={centroAnterior} target="_blank" rel="noreferrer noopener" className="inline-flex">
                <Button
                  variant="outline"
                  rightIcon={<ExternalLink className="size-4" aria-hidden="true" />}
                >
                  Centro anterior
                </Button>
              </a>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {consulta.isLoading && busqueda === '' ? <Skeleton className="h-64 w-full" /> : null}

      {encontrados === 0 ? (
        <Alert variant="info" title="Ningún reporte coincide">
          Prueba con otra palabra: el catálogo tiene {CATALOGO_REPORTES.length} reportes.
        </Alert>
      ) : null}

      {porFamilia.map((grupo) => (
        <Card key={grupo.familia}>
          <CardContent className="space-y-1 p-4">
            <div className="flex items-center justify-between gap-2 pb-1">
              <h2 className="text-sm font-semibold text-ink-800">{grupo.familia}</h2>
              <span className="text-xs text-ink-500">{grupo.reportes.length} reporte(s)</span>
            </div>

            <ul className="divide-y divide-ink-100">
              {grupo.reportes.map((reporte) => {
                const nota = notaDe(reporte);
                const urlAnterior = reporte.interno ? null : buildLegacyUrl(reporte.ruta);

                return (
                  <li
                    key={reporte.ruta}
                    className="flex flex-wrap items-center justify-between gap-3 py-2"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-2.5">
                      <span
                        aria-hidden="true"
                        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-brand-100"
                      >
                        <FileBarChart className="size-4" />
                      </span>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink-900">
                          {reporte.nombre}
                        </p>
                        {nota ? (
                          <p className="truncate text-xs text-ink-500" title={nota}>
                            {nota}
                          </p>
                        ) : null}
                        <p className="truncate font-mono text-[11px] text-ink-300">{reporte.ruta}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {reporte.interno ? (
                        <>
                          <Badge variant="brand">Nuevo panel</Badge>
                          <Link to={reporte.interno}>
                            <Button
                              size="sm"
                              rightIcon={<ArrowRight className="size-3.5" aria-hidden="true" />}
                            >
                              Abrir
                            </Button>
                          </Link>
                        </>
                      ) : (
                        <>
                          <Badge variant="outline" title="Sigue en el panel anterior">
                            <Lock className="size-3" aria-hidden="true" />
                            Migrando
                          </Badge>
                          {urlAnterior ? (
                            <a href={urlAnterior} target="_blank" rel="noreferrer noopener">
                              <Button
                                size="sm"
                                variant="outline"
                                rightIcon={<ExternalLink className="size-3.5" aria-hidden="true" />}
                              >
                                Panel anterior
                              </Button>
                            </a>
                          ) : (
                            <span className="text-xs text-ink-400">Sin enlace configurado</span>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ))}

      <p className="text-xs text-ink-500">
        Los reportes que siguen en el panel anterior se abren allí tal cual, sin perder nada; se
        migrarán uno a uno. Las consultas de comprobantes aceptados y no aceptados del índice
        anterior (envíos a SUNAT por fechas) siguen en el centro de reportes anterior.
        {consulta.data ? (
          <> Índice generado el {formatDateTimeValue(consulta.data.generado_at, true)}.</>
        ) : null}
      </p>
    </div>
  );
}
